import { stringKeyToTuple, tupleToStringKey } from 'js/lib/stringKeyTuple';
import { TIMESTAMP, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type LegacyTeam from 'bundles/author-assignment-grading/components/common/types/Team';
import GroupMember from 'bundles/author-groups/models/GroupMember';
import type { SubmissionDataGradingItemTaskV2Fragment } from 'bundles/grading/api/__generated__/SubmissionDataQueryV2';
import type {
  GradingItemTask,
  SubmissionDataGradingTask,
  TeamSubmitter,
  UserProfile,
  UserSubmitter,
} from 'bundles/grading/api/types';
import type { SGASubmitter, Team } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';

import _t from 'i18n!nls/grading';

/**
 * Sorts a list of submitters for display. The submitters list is previously
 * built by `constructSGASubmitter`, where `name` field follows this logic:
 *  - For teams: for anonymous - "anonymous", otherwise team's `name`.
 *  - For individuals: for anonymous - `externalId` or "anonymous", otherwise
 * `displayName` generated on the BE.
 *
 * The "sortSubmitterList" sorts by `name` for both, team and individual.
 *
 * @param { SGASubmitter[] } submitterList
 * @returns SGASubmitter[]
 */
export const sortSubmitterList = (submitterList: SGASubmitter[]): SGASubmitter[] => {
  const sortOptions = { numeric: true, ignorePunctuation: true };
  return submitterList
    .slice()
    .sort((submitter1, submitter2) => submitter1.name.localeCompare(submitter2.name, undefined, sortOptions));
};

/**
 * Returns submitter object for given submitterId if it exists within the given group
 *
 * @param { string } submitterId
 * @param { string } submitterGroup
 * @returns SGASubmitter?
 */
export const getSubmitterFromListById = (submitterId: string, submitterGroup: SGASubmitter[]) => {
  return submitterGroup.find((submitter: SGASubmitter) => submitterId === submitter.id);
};

/**
 * Creates a list of Group Members which is a type used in old grading UI components
 *
 *
 * @param { UserProfile[] } submitterList
 * @param { string } currentContextId
 * @returns GroupMember[]
 */
export const generateGroupMembers = (userProfiles: UserProfile[], currentContextId: string): GroupMember[] => {
  return userProfiles.map((submitter) => {
    return new GroupMember({
      id: submitter.userId + '~' + currentContextId,
      userId: parseInt(submitter.userId, 10),
      externalId: submitter.externalId || undefined,
      fullName: submitter.fullName,
      email: submitter.email || undefined,
    });
  });
};

/**
 * Creates a Legacy-Team object from Quick-Grader-Team object
 *
 * @param { Team } team
 * @returns LegacyTeam[]
 */
export const generateLegacyTeam = (team: Team): LegacyTeam => {
  return {
    members: team.userProfiles.map((userProfile) => parseInt(userProfile.userId, 10)),
    id: team.id,
    name: team.name,
  };
};

/**
 * Gets the "new" submitter id of style 'user~1234'/'team~1234'
 * from the legacy ids
 *
 * @param { string } legacySubmitterId
 * @returns string
 */
export const getNewSubmitterIdFromLegacyId = (legacySubmitterId: string): string => {
  const chunks = legacySubmitterId.split('~');
  return chunks.length > 1 ? `team~${chunks[chunks.length - 1]}` : `user~${chunks[chunks.length - 1]}`;
};

/**
 * Gets the legacy submitter id (route submitter id) of style '1234'/'PRIVATE_GROUP!~contextId~teamId'
 * from the new ids
 *
 * @param { string } newSubmitterId
 * @param { string } contextId
 * @returns string
 */
export const getLegacySubmitterIdFromNewId = (newSubmitterId: string, contextId: string): string => {
  return newSubmitterId.startsWith('team')
    ? `PRIVATE_GROUP!~${contextId}~${newSubmitterId.replace('team~', '')}`
    : newSubmitterId.replace('user~', '');
};

/**
 * What is scopedSubmitterId:
 * ("team" + ((scope_type + scope_id) + teamId))
 * scope = scope_type + scope_id = "PRIVATE_GROUP" + "groupId" = "PRIVATE_GROUP~groupId" = "PRIVATE_GROUP~KxiKLnI-EeyMoA557ooXyw"
 * scoped team id = scope + teamId = "PRIVATE_GROUP~groupId" + "teamId" + "PRIVATE_GROUP!~groupId~teamId" = "PRIVATE_GROUP!~KxiKLnI-EeyMoA557ooXyw~3Rv3G9BFRUGDkcXcLHV18Q"
 * scoped team id submitter = "team" + "scoped team id" = "team" + "PRIVATE_GROUP!~groupId~teamId" = "team~PRIVATE_GROUP!!~groupId!~teamId" = "team~PRIVATE_GROUP!!~KxiKLnI-EeyMoA557ooXyw!~3Rv3G9BFRUGDkcXcLHV18Q"
 *
 * @param routeSubmitterId Individual: `12345`, Team: `PRIVATE_GROUP!~KxiKLnI-EeyMoA557ooXyw~3Rv3G9BFRUGDkcXcLHV18Q`
 * @param gradingGroupId
 * @returns Individual `user~12345`, Team: `team~PRIVATE_GROUP!!~KxiKLnI-EeyMoA557ooXyw!~3Rv3G9BFRUGDkcXcLHV18Q`
 */
export const getScopedSubmitterIdFromRouteSubmitterId = (routeSubmitterId: string, gradingGroupId?: string): string => {
  return routeSubmitterId.indexOf('~') > -1
    ? tupleToStringKey(['team', getTeamRouteSubmitterIdWithGradingGroupId(routeSubmitterId, gradingGroupId)])
    : `user~${routeSubmitterId}`;
};

/**
 * The function will split the team routeSubmitterId (scoped team id) into separate parts, replace the groupId with
 * gradingGroupId if exists, and merge it back to the original format.
 *
 * @param routeSubmitterTeamId Team: `PRIVATE_GROUP!~groupId~teamId`
 * @param gradingGroupId
 * @returns `PRIVATE_GROUP!~gradingGroupId~teamId`
 */
const getTeamRouteSubmitterIdWithGradingGroupId = (
  routeSubmitterTeamId: string,
  gradingGroupId: string | undefined
): string => {
  if (!gradingGroupId) {
    return routeSubmitterTeamId;
  }

  // Split "PRIVATE_GROUP!~scopeId~teamId" into ["PRIVATE_GROUP~scopeId", "teamId"]
  const [scope, teamId] = stringKeyToTuple(routeSubmitterTeamId);
  // Split "PRIVATE_GROUP~scopeId" into ["PRIVATE_GROUP", "scopeId"]
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const [scopeType] = stringKeyToTuple(scope);

  // Create a new scope with the gradingGroupId instead
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const newScope = tupleToStringKey([scopeType, gradingGroupId]);

  // Merge back to the routeSubmitterTeamId (scoped team id)
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return tupleToStringKey([newScope, teamId]);
};

const gradingTaskIsFromSubmitterListQuery = (
  task: GradingItemTask | SubmissionDataGradingTask
): task is GradingItemTask => {
  return (task as GradingItemTask).latestSubmissionSummary !== undefined;
};

/**

 * Builds `SGASubmitter` we use in `useSubmitterList` and `useSubmissionData`.
 * Note, `id` prop is only for URL navigation purposes to match id used for
 * Legacy SGA.
 *
 * TODO:
 * 1 - reassess `id` value for the case of team assignment in Learner/Roster
 * flow. Quick Grader is not supported in the Learner/Roster flow.
 * 2 - Will need to transition into new team url using submitterId only.
 * Incorporate separate query for Learner/Roster flow to further filter userId
 * in use.
 *
 * @param task GradingItemTask returned from SubmitterList or SubmissionData query
 * @param contextId string. Aka "groupId"
 * @param isTeamAssignment whether SGA is team assignment
 * @param isAnonymized whether submission is anonymized
 * @returns SGASubmitter
 */
export const constructSGASubmitter = (
  task: GradingItemTask | SubmissionDataGradingTask,
  contextId: string,
  isTeamAssignment: boolean,
  isAnonymized: boolean
): SGASubmitter => {
  let submitter: SGASubmitter;

  const latestSubmissionInfo = gradingTaskIsFromSubmitterListQuery(task)
    ? {
        submissionStatus: task.latestSubmissionSummary?.status,
        submittedAt: task.latestSubmissionSummary?.submittedAt || null,
      }
    : {
        submissionStatus: task.latestSubmission?.status,
        submittedAt: task.latestSubmission?.submittedAt || null,
      };

  if (isTeamAssignment) {
    submitter = {
      id: getLegacySubmitterIdFromNewId(task?.submitterId, contextId),
      submitterId: task.submitterId,
      name: isAnonymized ? _t('Anonymous Team') : (task.submitter as TeamSubmitter).name,
      submissionDeadline: task.submissionDeadline,
      isSubmissionGraded: task.isSubmissionGraded,
      teamVersion: (task.submitter as TeamSubmitter).teamVersion,
      userProfiles: (task.submitter as TeamSubmitter).userProfiles,
      ...latestSubmissionInfo,
    };
  } else {
    submitter = {
      id: (task.submitter as UserSubmitter).userProfile.userId,
      submitterId: task.submitterId,
      name: isAnonymized
        ? (task.submitter as UserSubmitter).userProfile.externalId || _t('Anonymous Learner')
        : (task.submitter as UserSubmitter).userProfile.displayName,
      submissionDeadline: task.submissionDeadline,
      isSubmissionGraded: task.isSubmissionGraded,
      ...(task.submitter as UserSubmitter).userProfile,
      ...latestSubmissionInfo,
    };
  }

  return submitter;
};

export const constructSGASubmitterV2 = (
  task: SubmissionDataGradingItemTaskV2Fragment,
  isAnonymized: boolean
): SGASubmitter => {
  let submitter: SGASubmitter;

  const latestSubmissionInfo = {
    submissionStatus: task.latestSubmission?.status,
    submittedAt: task.latestSubmission?.submittedAt || null,
  };

  if (task.submitter.__typename === 'GroupGrading_TeamSubmitter') {
    submitter = {
      // ROUTE submitter ID: Expected: PRIVATE_GROUP!~KxiKLnI-EeyMoA557ooXyw~3Rv3G9BFRUGDkcXcLHV18Q
      // task.scopedSubmitterId comes as `team~PRIVATE_GROUP!!~KxiKLnI-EeyMoA557ooXyw!~3Rv3G9BFRUGDkcXcLHV18Q`
      // `stringKeyToTuple` helps to skip `team~` and unescape separators
      // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      id: stringKeyToTuple(task.scopedSubmitterId)[1],
      // Expected: Team: team~12345
      submitterId: getNewSubmitterIdFromLegacyId(task.scopedSubmitterId),
      name: isAnonymized ? _t('Anonymous Team') : task.submitter.name,
      submissionDeadline: {
        seconds: `${parseInt(formatDateTimeDisplay(task.submissionDeadline, TIMESTAMP), 10) / 1000}`,
      },
      isSubmissionGraded: task.isSubmissionGraded,
      teamVersion: task.submitter.teamVersion,
      userProfiles: task.submitter.userProfiles,
      ...latestSubmissionInfo,
    };
  } else {
    submitter = {
      // ROUTE submitter ID: Expected: 12345
      id: task.submitter.userProfile.userId,
      // Expected: user~12345
      submitterId: task.scopedSubmitterId,
      name: isAnonymized
        ? task.submitter.userProfile.externalId || _t('Anonymous Learner')
        : task.submitter.userProfile.displayName,
      submissionDeadline: {
        seconds: `${parseInt(formatDateTimeDisplay(task.submissionDeadline, TIMESTAMP), 10) / 1000}`,
      },
      isSubmissionGraded: task.isSubmissionGraded,
      ...task.submitter.userProfile,
      ...latestSubmissionInfo,
    };
  }

  return submitter;
};
